import { createContext, useContext, useEffect, useState } from 'react';

export interface EditorSettings {
  lineWrapping: boolean;
}

export interface EditorSettingsContextType {
  settings: EditorSettings;
  updateSettings: (updates: Partial<EditorSettings>) => void;
  resetSettings: () => void;
}

const DEFAULT_SETTINGS: EditorSettings = {
  lineWrapping: false,
};

const EditorSettingsContext = createContext<
  EditorSettingsContextType | undefined
>(undefined);

export const EditorSettingsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [settings, setSettings] = useState<EditorSettings>(() => {
    try {
      const savedSettings = localStorage.getItem('editorSettings');
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings);
        return { ...DEFAULT_SETTINGS, ...parsed };
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to load editor settings from localStorage:', error);
    }
    return DEFAULT_SETTINGS;
  });

  useEffect(() => {
    localStorage.setItem('editorSettings', JSON.stringify(settings));
  }, [settings]);

  const updateSettings = (updates: Partial<EditorSettings>) => {
    setSettings((prev) => ({ ...prev, ...updates }));
  };

  const resetSettings = () => {
    setSettings(DEFAULT_SETTINGS);
  };

  const value: EditorSettingsContextType = {
    settings,
    updateSettings,
    resetSettings,
  };

  return (
    <EditorSettingsContext.Provider value={value}>
      {children}
    </EditorSettingsContext.Provider>
  );
};

export const useEditorSettings = (): EditorSettingsContextType => {
  const context = useContext(EditorSettingsContext);
  if (context === undefined) {
    throw new Error(
      'useEditorSettings must be used within an EditorSettingsProvider',
    );
  }
  return context;
};
